import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  MenuItem,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import ApiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';

const PostJob = () => {
  const { currentUser } = useAuth();
  const { currencyCode } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [jobData, setJobData] = useState({
    title: '',
    company: '',
    location: '',
    type: 'full-time',
    category: '',
    experience: '',
    salary: {
      min: '',
      max: '',
      period: 'monthly',
    },
    description: '',
    requirements: '',
    responsibilities: '',
    benefits: '',
    isRemote: false,
    skills: '',
    deadline: '',
    // Application method fields
    applicationMethod: 'internal',
    externalApplicationUrl: '',
    applicationEmail: ''
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [formErrors, setFormErrors] = useState({});

  const jobTypes = [
    'full-time',
    'part-time',
    'contract',
    'temporary',
    'internship',
  ];

  const jobCategories = [
    'Technology',
    'Healthcare',
    'Finance',
    'Education',
    'Marketing',
    'Sales',
    'Engineering',
    'Design',
    'Other',
  ];

  const experienceLevels = [
    { value: 'entry-level', label: 'Entry Level' },
    { value: 'mid-level', label: 'Mid Level' },
    { value: 'senior-level', label: 'Senior Level' },
    { value: 'executive', label: 'Executive' },
  ];

  const handleChange = (event) => {
    const { name, value, checked } = event.target;
    if (name.startsWith('salary.')) {
      const salaryField = name.split('.')[1];
      setJobData({
        ...jobData,
        salary: {
          ...jobData.salary,
          [salaryField]: value,
        },
      });
    } else {
      setJobData({
        ...jobData,
        [name]: name === 'isRemote' ? checked : value,
      });
    }
  };

  useEffect(() => {
    setJobData(prev => ({
      ...prev,
      company: currentUser?.employerProfile?.companyName || currentUser?.companyName || prev.company,
    }));
  }, [currentUser]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!currentUser) {
      setSnackbar({
        open: true,
        message: 'You must be logged in to post a job',
        severity: 'error',
      });
      return;
    }

    // Basic required field validation (frontend guard)
    const errors = {};
    if (!jobData.title?.trim()) errors.title = 'Job title is required';
    if (!jobData.description?.trim()) errors.description = 'Job description is required';
    if (!jobData.category?.trim()) errors.category = 'Category is required';
    if (!jobData.type?.trim()) errors.type = 'Job type is required';
    if (!jobData.experience?.trim()) errors.experience = 'Experience level is required';
    if (!jobData.location?.trim()) errors.location = 'Location (city) is required';

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields.',
        severity: 'error',
      });
      return;
    } else {
      setFormErrors({});
    }

    setLoading(true);
    
    try {
      // Prepare job data for API
      const jobPayload = {
        title: jobData.title,
        description: jobData.description,
        shortDescription: jobData.description.substring(0, 200),
        companyName: jobData.company || currentUser?.employerProfile?.companyName || currentUser?.companyName || `${currentUser.firstName} ${currentUser.lastName}`,
        category: jobData.category,
        jobType: jobData.type,
        workLocation: jobData.isRemote ? 'remote' : 'on-site',
        experienceLevel: jobData.experience,
        location: {
          address: jobData.location,
          city: jobData.location,
          country: 'Zambia'
        },
        salary: {
          min: parseInt(jobData.salary.min) || 0,
          max: parseInt(jobData.salary.max) || 0,
          currency: currencyCode,
          period: jobData.salary.period || 'monthly'
        },
        requirements: jobData.requirements,
        responsibilities: jobData.responsibilities,
        benefits: jobData.benefits,
        skills: jobData.skills ? jobData.skills.split(',').map(skill => skill.trim()) : [],
        applicationDeadline: jobData.deadline ? new Date(jobData.deadline).toISOString() : null,
        employer: currentUser._id,
        status: 'pending', // Changed from 'active' to 'pending' for admin approval
        // Application method settings
        applicationMethod: jobData.applicationMethod,
        externalApplicationUrl: jobData.applicationMethod === 'external' ? jobData.externalApplicationUrl : '',
        applicationEmail: jobData.applicationMethod === 'email' ? jobData.applicationEmail : ''
      };

      console.log('Submitting job data:', jobPayload);
      
      const response = await ApiService.createJob(jobPayload);
      
      if (response.success) {
        setSnackbar({
          open: true,
          message: 'Job submitted successfully! It will be reviewed by our admin team before being published.',
          severity: 'success',
        });
        
        // Reset form
        setJobData({
          title: '',
          company: '',
          location: '',
          type: 'full-time',
          category: '',
          experience: '',
          salary: {
            min: '',
            max: '',
          },
          description: '',
          requirements: '',
          benefits: '',
          isRemote: false,
          skills: '',
          deadline: '',
          applicationMethod: 'internal',
          externalApplicationUrl: '',
          applicationEmail: ''
        });
      } else {
        throw new Error(response.message || 'Failed to create job');
      }
    } catch (error) {
      console.error('Error creating job:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to post job. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Post a New Job
      </Typography>

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="Job Title"
                  name="title"
                  value={jobData.title}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="Company Name"
                  name="company"
                  value={jobData.company}
                  onChange={handleChange}
                  disabled
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="Location"
                  name="location"
                  value={jobData.location}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  select
                  label="Job Type"
                  name="type"
                  value={jobData.type}
                  onChange={handleChange}
                >
                  {jobTypes.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  select
                  label="Category"
                  name="category"
                  value={jobData.category}
                  onChange={handleChange}
                >
                  {jobCategories.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                select
                label="Experience Level"
                name="experience"
                value={jobData.experience}
                onChange={handleChange}
                error={!!formErrors.experience}
                helperText={formErrors.experience || ''}
              >
                {experienceLevels.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Minimum Salary"
                  name="salary.min"
                  type="number"
                  value={jobData.salary.min}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">{currencyCode}</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Maximum Salary"
                  name="salary.max"
                  type="number"
                  value={jobData.salary.max}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">{currencyCode}</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Pay Period"
                  name="salary.period"
                  value={jobData.salary.period}
                  onChange={handleChange}
                >
                  <MenuItem value="hourly">Per Hour</MenuItem>
                  <MenuItem value="monthly">Per Month</MenuItem>
                  <MenuItem value="yearly">Per Year</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Responsibilities"
                  name="responsibilities"
                  value={jobData.responsibilities}
                  onChange={handleChange}
                  helperText="Enter one per line or use numbering"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  multiline
                  rows={4}
                  label="Job Description"
                  name="description"
                  value={jobData.description}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  multiline
                  rows={4}
                  label="Requirements"
                  name="requirements"
                  value={jobData.requirements}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Benefits"
                  name="benefits"
                  value={jobData.benefits}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Required Skills"
                  name="skills"
                  value={jobData.skills}
                  onChange={handleChange}
                  helperText="Enter skills separated by commas"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Application Deadline"
                  name="deadline"
                  value={jobData.deadline}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={jobData.isRemote}
                      onChange={handleChange}
                      name="isRemote"
                    />
                  }
                  label="This is a remote position"
                />
              </Grid>
            </Grid>

            {/* How to Apply Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                HOW TO APPLY
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Apply By"
                name="applicationMethod"
                value={jobData.applicationMethod}
                onChange={handleChange}
                helperText="Choose how applicants should apply"
              >
                <MenuItem value="internal">Apply On JobiT (Recommended)</MenuItem>
                <MenuItem value="external">Link</MenuItem>
                <MenuItem value="email">Email</MenuItem>
              </TextField>
            </Grid>

            {jobData.applicationMethod === 'external' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="External Application Link"
                  name="externalApplicationUrl"
                  value={jobData.externalApplicationUrl}
                  onChange={handleChange}
                  placeholder="https://example.com/apply"
                />
              </Grid>
            )}

            {jobData.applicationMethod === 'email' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Application Email"
                  name="applicationEmail"
                  value={jobData.applicationEmail}
                  onChange={handleChange}
                  placeholder="jobs@example.com"
                />
              </Grid>
            )}

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                disabled={loading}
              >
                {loading ? 'Posting...' : 'Post Job'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PostJob;